import { useCallback, useMemo } from "react";
import { Position } from "../../../../../utils/models";
import { useCertificationDefaults } from "../useCertificationDefaults";
import { normalizeRoleString } from "./utils";

/**
 * The `position` a cert-mapping upsert carries now that the admin no longer picks one (DAT-804).
 *
 * A value is still required — the column is NOT NULL, the wire field is required, and
 * `department.hris_positions` still keys the special-job PPD overrides. DAT-464 removed the only
 * thing that made the admin's answer matter (enabled JOBS now decide which employees sync), so it
 * is derived rather than asked for.
 *
 * Order: the row's own value → the global default for that role string → the server default.
 * `DontSync` is never derived; it is an explicit per-role-string opt-out.
 */
export function derivePosition(
	roleString: string,
	currentPosition: Position | null | undefined,
	defaultPositionByRoleString: ReadonlyMap<string, Position>,
): Position {
	if (currentPosition && currentPosition !== Position.DontSync) return currentPosition;
	const fromGlobalDefaults = defaultPositionByRoleString.get(normalizeRoleString(roleString));
	if (fromGlobalDefaults && fromGlobalDefaults !== Position.DontSync) return fromGlobalDefaults;
	// DEFAULT: mirrors the server default on `department_certification_job.position`
	// (backend/database/jobs.py) so a role string the global table has never seen still saves.
	return Position.Nursing;
}

/** Normalized role string → the position the global merged table resolves it to. */
export function buildDefaultPositionByRoleString(defaults: { roleString: string; position?: Position | null }[]): Map<string, Position> {
	const map = new Map<string, Position>();
	for (const mapping of defaults) {
		// The sentinel is skipped rather than stored: `derivePosition` rejects it anyway, so storing it
		// would let a leading DontSync row shadow a later real position for the same role string and
		// send the save to the server default instead.
		if (!mapping.position || mapping.position === Position.DontSync) continue;
		const key = normalizeRoleString(mapping.roleString);
		// First real position wins — the global table is effectively one position per role string.
		if (!map.has(key)) map.set(key, mapping.position);
	}
	return map;
}

/**
 * Binds {@link derivePosition} to the global merged defaults so call sites only pass what they
 * know: the role string and the position already on the row.
 *
 * The query's `isLoading` AND `isError` ride along on purpose. Both HRIS screens have to gate saving
 * until the defaults land, and a REJECTED load looks exactly like a successful empty one from here:
 * `isLoading` goes false with `data` undefined, so the map is empty and every derivation falls through
 * to `Position.Nursing`. The error snackbar alone does not stop the save, so the screens block on both.
 *
 * Reading the status from a second `useCertificationDefaults()` call made the screen two observers of
 * the one cache entry — so a single failed load ran `useCertificationDefaults`'s error effect twice and
 * `SnackbarContext` (which appends, it does not dedupe) showed two identical toasts. Owning the
 * subscription here means each screen calls the hook exactly once.
 */
export function useDerivePosition(): {
	derivePositionFor: (roleString: string, currentPosition: Position | null | undefined) => Position;
	isLoading: boolean;
	isError: boolean;
} {
	const { data: globalDefaults, isLoading, isError } = useCertificationDefaults();
	const defaultPositionByRoleString = useMemo(() => buildDefaultPositionByRoleString(globalDefaults ?? []), [globalDefaults]);
	const derivePositionFor = useCallback(
		(roleString: string, currentPosition: Position | null | undefined) => derivePosition(roleString, currentPosition, defaultPositionByRoleString),
		[defaultPositionByRoleString],
	);
	return { derivePositionFor, isLoading, isError };
}
